import LinkIcon from '@mui/icons-material/Link';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Link, IconButton as MuiIconButton, Tooltip } from '@mui/material';
import { SidebarCard, SidebarHeader } from 'app/common/components/sidebar';
import { join } from 'app/common/utils/path';
import useLocalStorage from 'app/common/utils/use-local-storage';
import DateString from 'app/content/components/date-string';
import type React from 'react';
import styled from 'styled-components';
import LoadingRectangles from '../../../common/components/loading-placeholders/loading-rects.js';
import type { LiveLinksProps, SiteLiveLinks } from '../../types.js';
import SiteDropdown from './site-dropdown.js';

const RouteContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '.5rem',
  flex: '1 1 auto'
});

const Container = styled(Box)({
  width: '100%',
  marginBottom: '2rem'
});

const IconButton = styled(MuiIconButton)({
  alignSelf: 'center',
  color: '#607d8b'
});

const RoutePath = styled(Box)(({ theme }) => ({
  fontSize: '1.1rem',
  padding: '.5rem',
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  backgroundColor: 'transparent',
  transition: 'background-color .1s linear',
  color: theme.palette.grey['800'],
  lineHeight: '2rem',
  height: '3rem',
  flex: '1 1 auto',
  minWidth: 0,
  userSelect: 'none',

  '&:hover': {
    backgroundColor: theme.palette.grey['100'],
    textDecoration: 'none'
  }
}));

const RouteLink = styled(Link)(({ theme }) => ({
  color: theme.palette.grey['800'],
  width: '100%'
}));

const LinkText = styled(Box)(({ theme }) => ({
  color: theme.palette.grey['800'],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
}));

function isEmpty(sites: SiteLiveLinks[]): boolean {
  return !sites.find((site) => site.routes.length);
}

const LiveLinks: React.FC<LiveLinksProps> = ({
  sites,
  usageState,
  previewState,
  projectId,
  setPreviewRoute
}: LiveLinksProps): JSX.Element | null => {
  const ready = (usageState.loaded && !usageState.loading) || (previewState.loaded && !previewState.loading);
  const initialSiteId = sites.length ? sites[0].siteId : undefined;
  const [selectedSite = initialSiteId, setSelectedSite] = useLocalStorage<string>(`${projectId}:selected-static-site`);
  const site = sites.find((site) => site.siteId === selectedSite);

  let content: React.ReactNode;

  if (!ready) {
    content = (
      <Box pb="1rem">
        <LoadingRectangles height="2rem" />
      </Box>
    );
  } else if (isEmpty(sites)) {
    return null;
  } else if (site) {
    const { routes, baseUrl, deployedAt, siteId } = site;
    const tooltip = deployedAt ? (
      <span>
        {'Deployed at '}
        <DateString date={deployedAt} formatString="yyyy-MM-dd, h:mm a z" />
      </span>
    ) : (
      'Has not been deployed'
    );

    content = routes.map(({ path, preview, routeName }) => (
      <RouteContainer key={`${routeName}/${path}`}>
        <Tooltip title={tooltip}>
          <RoutePath>
            {baseUrl && deployedAt ? (
              <RouteLink href={baseUrl && join(baseUrl, path)} target="_blank" rel="noopener noreferrer">
                <LinkText>{path}</LinkText>
              </RouteLink>
            ) : (
              <LinkText>{path}</LinkText>
            )}
          </RoutePath>
        </Tooltip>
        {preview && (
          <Tooltip title={`Preview ${path}`}>
            <IconButton
              size="small"
              onClick={(): void => {
                setPreviewRoute({ siteId, routeName });
              }}
            >
              <VisibilityIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
      </RouteContainer>
    ));
  }

  return (
    <Container>
      <SidebarHeader justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <LinkIcon fontSize="small" />
          <Box ml="0.5rem">Static Site</Box>
        </Box>
        {sites.length ? <SiteDropdown sites={sites} selected={selectedSite} onChange={setSelectedSite} /> : null}
      </SidebarHeader>
      <SidebarCard>{content}</SidebarCard>
    </Container>
  );
};

export default LiveLinks;
